import React, { useState, useMemo } from 'react';
import {
  FileText, Download, CheckCircle2, AlertTriangle, Play, User, Server, X,
  Package, Search, ChevronUp, ChevronDown, ChevronLeft, ChevronRight,
  Calendar, Filter, RefreshCw, Loader2, AlertCircle, Columns, Link2, Copy, ExternalLink
} from 'lucide-react';
import { auditEvents as rawAuditEvents, evidenceBundles, workflowRuns } from '../data/automationData';
import { Button } from '../components/ui/button';
import { AsyncButton } from '../components/ui/async-button';
import { toast } from 'sonner';
import { Badge } from '../components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '../components/ui/tooltip';

const auditEvents = rawAuditEvents || [];

const eventTypeIcons = {
  'workflow.started': Play,
  'workflow.completed': CheckCircle2,
  'workflow.step.completed': CheckCircle2,
  'connector.action': Server,
  'approval.granted': User,
  'policy.violation': AlertTriangle
};

const severityColors = {
  info: { bg: 'bg-primary/10', text: 'text-primary' },
  warning: { bg: 'bg-slate-200', text: 'text-slate-700' },
  critical: { bg: 'bg-slate-900', text: 'text-white' }
};

const ITEMS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

const EmptyState = ({ icon: Icon, title, description, action }) => (
  <div className="flex flex-col items-center justify-center py-12 px-4">
    <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-4">
      <Icon className="w-6 h-6 text-slate-400" />
    </div>
    <h3 className="text-sm font-semibold text-slate-900 mb-1">{title}</h3>
    <p className="text-sm text-slate-500 text-center max-w-sm mb-4">{description}</p>
    {action}
  </div>
);

const LoadingState = () => (
  <div className="flex flex-col items-center justify-center py-12 px-4">
    <Loader2 className="w-8 h-8 text-primary animate-spin mb-4" />
    <p className="text-sm text-slate-500">Loading audit events...</p>
  </div>
);

const ErrorState = ({ onRetry }) => (
  <div className="flex flex-col items-center justify-center py-12 px-4">
    <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-4">
      <AlertCircle className="w-6 h-6 text-slate-600" />
    </div>
    <h3 className="text-sm font-semibold text-slate-900 mb-1">Failed to load audit events</h3>
    <p className="text-sm text-slate-500 text-center max-w-sm mb-4">
      There was an error loading the audit log. Please try again.
    </p>
    <Button variant="outline" size="sm" onClick={onRetry}>
      <RefreshCw className="w-4 h-4 mr-1" />
      Retry
    </Button>
  </div>
);

const ColumnSelector = ({ columns, visibleColumns, onToggle, onClose }) => (
  <div className="absolute right-0 top-full mt-1 z-10 bg-white rounded-lg shadow-lg border border-slate-200 p-2 w-48">
    <div className="flex items-center justify-between px-2 py-1 mb-1">
      <span className="text-xs font-medium text-slate-700">Show columns</span>
      <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
        <X className="w-3 h-3" />
      </button>
    </div>
    {columns.map((col) => (
      <label key={col.key} className="flex items-center gap-2 px-2 py-1.5 hover:bg-slate-50 rounded cursor-pointer">
        <input
          type="checkbox"
          checked={visibleColumns.includes(col.key)}
          onChange={() => onToggle(col.key)}
          className="rounded border-slate-300"
        />
        <span className="text-sm text-slate-700">{col.label}</span>
      </label>
    ))}
  </div>
);

export const Audit = () => {
  const [activeTab, setActiveTab] = useState('events');
  const [selectedBundle, setSelectedBundle] = useState(null);
  const [showBundleGenerator, setShowBundleGenerator] = useState(false);
  const [selectedSource, setSelectedSource] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState('timestamp');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [dateRange, setDateRange] = useState('all');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(['action', 'target', 'actor', 'timestamp', 'severity', 'correlationId']);

  const columns = [
    { key: 'action', label: 'Action', sortable: true },
    { key: 'target', label: 'Target', sortable: true },
    { key: 'actor', label: 'Actor', sortable: true },
    { key: 'source', label: 'Source', sortable: true },
    { key: 'timestamp', label: 'Timestamp', sortable: true },
    { key: 'severity', label: 'Severity', sortable: true },
    { key: 'correlationId', label: 'Correlation ID', sortable: true }
  ];

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const filteredAndSortedEvents = useMemo(() => {
    let events = [...auditEvents];

    if (filterType !== 'all') {
      events = events.filter(e => e.type.startsWith(filterType));
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      events = events.filter(e =>
        e.action?.toLowerCase().includes(query) ||
        e.target?.toLowerCase().includes(query) ||
        e.actor?.toLowerCase().includes(query) ||
        e.details?.toLowerCase().includes(query)
      );
    }

    if (dateRange !== 'all') {
      const now = new Date();
      const cutoff = new Date();
      switch (dateRange) {
        case '1h': cutoff.setHours(now.getHours() - 1); break;
        case '24h': cutoff.setDate(now.getDate() - 1); break;
        case '7d': cutoff.setDate(now.getDate() - 7); break;
        case '30d': cutoff.setDate(now.getDate() - 30); break;
        default: break;
      }
      events = events.filter(e => new Date(e.timestamp) >= cutoff);
    }

    events.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      if (sortField === 'timestamp') {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      }
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return events;
  }, [filterType, searchQuery, dateRange, sortField, sortDirection]);

  const paginatedEvents = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredAndSortedEvents.slice(start, start + itemsPerPage);
  }, [filteredAndSortedEvents, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredAndSortedEvents.length / itemsPerPage);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const handleColumnToggle = (key) => {
    setVisibleColumns(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const handleExportCSV = async () => {
    await new Promise(resolve => setTimeout(resolve, 800));
    const headers = columns.filter(c => visibleColumns.includes(c.key)).map(c => c.label);
    const rows = filteredAndSortedEvents.map(event =>
      visibleColumns.map(key => {
        if (key === 'timestamp') return new Date(event[key]).toISOString();
        return event[key] || '';
      })
    );

    const csv = [headers.join(','), ...rows.map(r => r.map(c => `"${c}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-log-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Audit log exported', { description: `${filteredAndSortedEvents.length} events exported to CSV` });
  };

  const handleRetry = () => {
    setError(null);
    setIsLoading(true);
    setTimeout(() => setIsLoading(false), 1000);
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ChevronUp className="w-3 h-3 text-slate-300" />;
    return sortDirection === 'asc'
      ? <ChevronUp className="w-3 h-3 text-slate-700" />
      : <ChevronDown className="w-3 h-3 text-slate-700" />;
  };

  const renderBundleDetail = (bundle) => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-lg border border-slate-200 w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">{bundle.name}</h2>
            <p className="text-sm text-slate-500">Generated: {new Date(bundle.createdAt).toLocaleString()}</p>
          </div>
          <button onClick={() => setSelectedBundle(null)} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[60vh]">
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="p-4 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-500 mb-1">Bundle ID</p>
              <p className="font-mono text-sm">{bundle.id}</p>
            </div>
            <div className="p-4 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-500 mb-1">Related</p>
              <p className="font-medium">{bundle.relatedId}</p>
            </div>
            <div className="p-4 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-500 mb-1">Type</p>
              <p className="font-medium capitalize">{bundle.type}</p>
            </div>
            <div className="p-4 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-500 mb-1">Created By</p>
              <p className="font-medium">{bundle.createdBy}</p>
            </div>
          </div>

          <h3 className="font-semibold mb-3">Bundle Contents ({bundle.totalItems} items)</h3>
          <div className="space-y-2 mb-6">
            {bundle.items.map((item, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <FileText className="w-4 h-4 text-slate-500" />
                  <span className="text-sm">{item.type}</span>
                </div>
                <span className="text-sm text-slate-500">{item.count} items</span>
              </div>
            ))}
          </div>
        </div>

        <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex gap-2">
          <Button variant="outline" onClick={() => setSelectedBundle(null)}>
            Close
          </Button>
          <Button variant="outline">
            <Download className="w-4 h-4 mr-1" />
            PDF
          </Button>
          <Button variant="outline">
            <Download className="w-4 h-4 mr-1" />
            JSON
          </Button>
          <Button>
            <Download className="w-4 h-4 mr-1" />
            Download All
          </Button>
        </div>
      </div>
    </div>
  );

  const renderBundleGenerator = () => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-lg border border-slate-200 w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900">Generate Evidence Bundle</h2>
          <button onClick={() => setShowBundleGenerator(false)} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[60vh]">
          <p className="text-sm text-slate-600 mb-6">Select a workflow run or incident to generate an evidence bundle.</p>

          <h3 className="font-semibold mb-3">Recent Workflow Runs</h3>
          <div className="space-y-2 mb-6">
            {(workflowRuns || []).filter(r => r.status === 'completed').map((run) => (
              <div
                key={run.id}
                onClick={() => setSelectedSource(run.id)}
                className={`p-4 border rounded-lg cursor-pointer transition-all ${
                  selectedSource === run.id
                    ? 'border-primary bg-primary/5'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{run.id} - {run.workflowName}</p>
                    <p className="text-sm text-slate-500">{run.target}</p>
                  </div>
                  <span className="text-xs text-slate-400">{new Date(run.completedAt).toLocaleDateString()}</span>
                </div>
              </div>
            ))}
          </div>

          {selectedSource && (
            <div className="p-4 bg-primary/10 border border-primary/20 rounded-lg">
              <p className="text-sm text-slate-700">
                Bundle will include: Workflow run record, approval records, audit events, connector logs, and policy checks.
              </p>
            </div>
          )}
        </div>

        <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex gap-2">
          <Button variant="outline" onClick={() => setShowBundleGenerator(false)}>
            Cancel
          </Button>
          <AsyncButton
            onClick={async () => {
              await new Promise(resolve => setTimeout(resolve, 1500));
              toast.success('Evidence bundle generated', { description: 'Bundle is ready for download' });
              setShowBundleGenerator(false);
            }}
            disabled={!selectedSource}
            loadingText="Generating..."
            successMessage="Bundle generated successfully"
          >
            Generate Bundle
          </AsyncButton>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <FileText className="w-5 h-5 text-slate-500" />
            Audit
          </h1>
          <p className="text-sm text-slate-500 mt-1">Unified audit log and evidence bundles</p>
        </div>
        <Button onClick={() => setShowBundleGenerator(true)} size="sm">
          <Package className="w-4 h-4 mr-1" />
          Generate Evidence Bundle
        </Button>
      </div>

      {selectedBundle && renderBundleDetail(selectedBundle)}
      {showBundleGenerator && renderBundleGenerator()}

      <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit">
        {['events', 'bundles'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors capitalize ${
              activeTab === tab
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            {tab === 'events' ? 'Audit Events' : 'Evidence Bundles'}
          </button>
        ))}
      </div>

      {activeTab === 'events' && (
        <>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex-1 min-w-[200px] relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                placeholder="Search events..."
                className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </div>

            <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5">
              {[
                { value: 'all', label: 'All' },
                { value: 'workflow', label: 'Workflows' },
                { value: 'connector', label: 'Connectors' },
                { value: 'approval', label: 'Approvals' },
                { value: 'policy', label: 'Policy' }
              ].map((filter) => (
                <button
                  key={filter.value}
                  onClick={() => { setFilterType(filter.value); setCurrentPage(1); }}
                  className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                    filterType === filter.value
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-slate-400" />
              <select
                value={dateRange}
                onChange={(e) => { setDateRange(e.target.value); setCurrentPage(1); }}
                className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary/20"
              >
                <option value="all">All time</option>
                <option value="1h">Last hour</option>
                <option value="24h">Last 24 hours</option>
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
              </select>
            </div>

            <div className="relative">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowColumnSelector(!showColumnSelector)}
              >
                <Columns className="w-4 h-4 mr-1" />
                Columns
              </Button>
              {showColumnSelector && (
                <ColumnSelector
                  columns={columns}
                  visibleColumns={visibleColumns}
                  onToggle={handleColumnToggle}
                  onClose={() => setShowColumnSelector(false)}
                />
              )}
            </div>

            <AsyncButton
              variant="outline"
              size="sm"
              onClick={handleExportCSV}
              loadingText="Exporting..."
            >
              <Download className="w-4 h-4 mr-1" />
              Export CSV
            </AsyncButton>
          </div>

          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
            {isLoading ? (
              <LoadingState />
            ) : error ? (
              <ErrorState onRetry={handleRetry} />
            ) : filteredAndSortedEvents.length === 0 ? (
              <EmptyState
                icon={FileText}
                title="No audit events found"
                description={searchQuery || filterType !== 'all'
                  ? "Try adjusting your search or filters to find what you're looking for."
                  : "Audit events will appear here as actions are performed in the system."}
                action={
                  (searchQuery || filterType !== 'all') && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { setSearchQuery(''); setFilterType('all'); setDateRange('all'); }}
                    >
                      Clear filters
                    </Button>
                  )
                }
              />
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-100">
                        {columns.filter(c => visibleColumns.includes(c.key)).map((col) => (
                          <th
                            key={col.key}
                            className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors"
                            onClick={() => col.sortable && handleSort(col.key)}
                          >
                            <div className="flex items-center gap-1">
                              {col.label}
                              {col.sortable && <SortIcon field={col.key} />}
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {paginatedEvents.map((event) => {
                        const EventIcon = eventTypeIcons[event.type] || FileText;
                        const colors = severityColors[event.severity] || severityColors.info;

                        return (
                          <tr key={event.id} className="hover:bg-slate-50 transition-colors">
                            {visibleColumns.includes('action') && (
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-3">
                                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${colors.bg}`}>
                                    <EventIcon className={`w-4 h-4 ${colors.text}`} />
                                  </div>
                                  <div>
                                    <p className="text-sm font-medium text-slate-900">{event.action}</p>
                                    <p className="text-xs text-slate-500 line-clamp-1">{event.details}</p>
                                  </div>
                                </div>
                              </td>
                            )}
                            {visibleColumns.includes('target') && (
                              <td className="px-4 py-3">
                                <span className="text-sm text-slate-700">{event.target}</span>
                              </td>
                            )}
                            {visibleColumns.includes('actor') && (
                              <td className="px-4 py-3">
                                <span className="text-sm text-slate-700">{event.actor}</span>
                              </td>
                            )}
                            {visibleColumns.includes('source') && (
                              <td className="px-4 py-3">
                                <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-600 rounded">
                                  {event.source}
                                </span>
                              </td>
                            )}
                            {visibleColumns.includes('timestamp') && (
                              <td className="px-4 py-3 text-right">
                                <p className="text-sm text-slate-700">{new Date(event.timestamp).toLocaleTimeString()}</p>
                                <p className="text-xs text-slate-400">{new Date(event.timestamp).toLocaleDateString()}</p>
                              </td>
                            )}
                            {visibleColumns.includes('severity') && (
                              <td className="px-4 py-3">
                                <span className={`text-xs px-2 py-0.5 rounded capitalize ${colors.bg} ${colors.text}`}>
                                  {event.severity}
                                </span>
                              </td>
                            )}
                            {visibleColumns.includes('correlationId') && (
                              <td className="px-4 py-3">
                                {event.correlationId || event.id ? (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <button
                                        onClick={() => copyToClipboard(event.correlationId || event.id)}
                                        className="group flex items-center gap-1 font-mono text-xs text-slate-600 hover:text-primary bg-slate-50 px-2 py-1 rounded"
                                      >
                                        <Link2 className="w-3 h-3" />
                                        {(event.correlationId || event.id).slice(0, 8)}...
                                        <Copy className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                                      </button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p className="font-mono text-xs">{event.correlationId || event.id}</p>
                                      <p className="text-xs text-muted-foreground mt-1">Click to copy</p>
                                    </TooltipContent>
                                  </Tooltip>
                                ) : (
                                  <span className="text-xs text-slate-400">—</span>
                                )}
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 bg-slate-50">
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-slate-500">
                      Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredAndSortedEvents.length)} of {filteredAndSortedEvents.length} events
                    </span>
                    <select
                      value={itemsPerPage}
                      onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                      className="text-xs border border-slate-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary/20"
                    >
                      {ITEMS_PER_PAGE_OPTIONS.map((n) => (
                        <option key={n} value={n}>{n} per page</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                    >
                      First
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="px-3 py-1 text-sm text-slate-600">
                      Page {currentPage} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages}
                    >
                      Last
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </>
      )}

      {activeTab === 'bundles' && (
        <>
          {(evidenceBundles || []).length === 0 ? (
            <div className="bg-white rounded-lg border border-slate-200">
              <EmptyState
                icon={Package}
                title="No evidence bundles yet"
                description="Generate evidence bundles from workflow runs or incidents to create audit-ready documentation."
                action={
                  <Button size="sm" onClick={() => setShowBundleGenerator(true)}>
                    <Package className="w-4 h-4 mr-1" />
                    Generate Bundle
                  </Button>
                }
              />
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {(evidenceBundles || []).map((bundle) => (
                <div
                  key={bundle.id}
                  className="bg-white rounded-lg border border-slate-200 p-5 hover:border-primary/30 hover:shadow-lg hover:shadow-primary/5 transition-all cursor-pointer"
                  onClick={() => setSelectedBundle(bundle)}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Package className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{bundle.id}</p>
                      <p className="text-xs text-slate-500">{bundle.type}</p>
                    </div>
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">{bundle.name}</h3>
                  <div className="flex items-center justify-between text-sm text-slate-500 mb-4">
                    <span>{bundle.totalItems} items</span>
                    <span>{new Date(bundle.createdAt).toLocaleDateString()}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="px-2 py-0.5 bg-primary/10 text-primary rounded text-xs font-medium">
                      {bundle.status}
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toast.success('Download started', { description: 'Your bundle is being prepared...' });
                      }}
                      className="text-primary hover:underline text-sm flex items-center gap-1"
                    >
                      <Download className="w-3.5 h-3.5" />
                      Download
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Audit;
